import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAgency } from '@/auth/AgencyProvider';
import { usePermissions } from '@/auth/PermissionsProvider';
import { useMyBillingStatus } from '@/data/useBilling';
import { dateYearInZone } from '@/lib/dates';
import { moneyCents } from '@/lib/format';

/**
 * agency-billing T4.2 (design §9, F8): the current agency's payment is past due and grace is running. Shown
 * to whoever can pay (admin, or a billing.manage override: `can_pay` of my_billing_status), next to the
 * other layout banners. A role that cannot pay never asks. The grace day is the agency's local day (the
 * instant is the END of grace, so the day shown is the last day to pay). A failed read shows nothing.
 */
export function PastDueBanner() {
  const { t, i18n } = useTranslation();
  const agency = useAgency();
  const { can } = usePermissions();
  const mayPay = agency.status === 'ready' && can('billing.manage');
  const { rows } = useMyBillingStatus(mayPay);
  if (agency.status !== 'ready' || !mayPay) return null;
  const row = rows.find((r) => r.agencyId === agency.agency.id);
  if (!row || row.status !== 'past_due' || !row.canPay) return null;

  const locale = i18n.resolvedLanguage === 'es' ? 'es-US' : 'en-US';
  const lastDay = row.graceEndsAt ? new Date(new Date(row.graceEndsAt).getTime() - 1).toISOString() : null;
  const date = lastDay ? dateYearInZone(lastDay, row.timeZone, locale) : '';
  const amount = row.amountDueCents !== null && row.amountDueCents > 0 ? moneyCents(row.amountDueCents / 100) : null;
  const text = !date
    ? t('billing.pastDue.bannerShort', { agency: row.agencyName })
    : amount
      ? t('billing.pastDue.banner', { agency: row.agencyName, amount, date })
      : t('billing.pastDue.bannerNoAmount', { agency: row.agencyName, date });

  return (
    <section
      aria-label={t('billing.pastDue.label')}
      className="mb-4 flex flex-col gap-3 rounded-md border border-hairline bg-card px-4 py-3 shadow-card sm:flex-row sm:items-center"
    >
      <CircleAlert className="hidden size-5 shrink-0 text-danger sm:block" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-base text-ink">{text}</p>
      {row.payUrl ? (
        <Button asChild size="sm">
          <a href={row.payUrl} rel="noopener noreferrer">
            {t('billing.pastDue.payNow')}
          </a>
        </Button>
      ) : (
        <Button asChild size="sm" variant="outline">
          <Link to="/settings/billing">{t('billing.pastDue.open')}</Link>
        </Button>
      )}
    </section>
  );
}
